import { Dropdown, Table, Form, Button, InputGroup } from "react-bootstrap";
import { CSVLink } from "react-csv";
import {
  ExportIcon,
  FilterTwoIcon,
  FilterCollapseIcon,
  ExcelIcon,
} from "../../Icons";
import "../../../assets/scss/reports/cashbook.scss";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import CachedIcon from "mdi-react/CachedIcon";
import PinIcon from "mdi-react/PinIcon";
import PinOutlineIcon from "mdi-react/PinOutlineIcon";
import useDebounce, {
  useLocalStorage,
  useQueryParams,
  useScrollTop,
} from "../../../utils/hooks";
import { useQuery } from "react-query";
import { endOfMonth, format, startOfMonth } from "date-fns";
import ReactPaginate from "react-paginate";
import currency from "currency.js";
import Select from "react-select";
import {
  customerFullName,
  paginationOptions,
  reactSelectTheme,
} from "../../../utils/helpers";
import queryString from "query-string";
import { useAuth } from "../../../hooks/useAuth";
import { isEmpty } from "lodash";
import NoTableItem from "../../utils/NoTableItem";
import { useDownloadExcel } from "../../../hooks/useDownloadExcel";
import ModalLoader from "../../utils/ModalLoader";
import VendorSelector from "../../Vendor/VendorSelector";
import { Popover } from "react-tiny-popover";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import MonthYearPicker from "../../utils/MonthYearPicker";

// One entry per source table this report consolidates. Drives both the
// column-header/cell background color and the legend above the table, so
// a user can see at a glance which system a field came from.
const COLUMN_GROUPS = {
  vendorLedger: { label: "Vendor", color: "#eaf3ff" },
  vendorPaymentDue: { label: "Vendor Payment Due", color: "#ffe9d6" },
  purchase: { label: "Purchase History", color: "#eefbea" },
  batch: { label: "Stock Batches", color: "#fff8e1" },
  items: { label: "Items", color: "#e6fbf8" },
  sale: { label: "Sales", color: "#fdeaea" },
  payment: { label: "Payment Due", color: "#f2eafc" },
};

// Inline (no external CSS) compact-cell styling — this table has a lot of
// columns, so cells are kept small/single-line and only widen if content
// forces it.
const COMPACT_CELL_STYLE = {
  padding: "4px 8px",
  // fontSize: "0.78rem",
  lineHeight: 1.3,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
};

const cellStyle = group => ({
  ...COMPACT_CELL_STYLE,
  backgroundColor: COLUMN_GROUPS[group].color,
});

// Stable reference so the useQuery default doesn't create a new `rows` array
// (and retrigger effects keyed on it) on every render while data is loading.
const EMPTY_REPORT_DATA = { count: 0, rows: [], branch: [] };

const formatMoney = value =>
  value || value === 0 ? currency(value, { symbol: "" }).format() : "-";

const formatDateValue = value =>
  value ? format(new Date(value), "MMM dd, yyyy") : "-";

// VendorPaymentDue installments: /setup-atc splits one invoice's outstanding
// balance into several installments sharing the same Trans_ID, so the
// backend groups them into `row.vendorPaymentDue` (an array) instead of a
// single record. These helpers roll that array up into single cell values.
const sumInstallments = (installments, field) =>
  (installments || []).reduce(
    (total, el) => currency(total).add(el?.[field] || 0).value,
    0
  );

const nextInstallmentDue = installments => {
  const outstanding = (installments || [])
    .filter(el => Number(el.Balance) > 0)
    .sort((a, b) => new Date(a.DueDate) - new Date(b.DueDate));
  return outstanding[0]?.DueDate || null;
};

const vendorPaymentDueStatus = installments => {
  if (!installments || installments.length === 0) return "-";
  const totalPaid = sumInstallments(installments, "AmountPaid");
  const totalBalance = sumInstallments(installments, "Balance");
  if (totalBalance <= 0) return "Fully Paid";
  if (totalPaid > 0) return "Partially Paid";
  return "Outstanding";
};

export default function AtcPurchaseSalesPayment() {
  useScrollTop();

  const initialFilterParams = {
    invoiceNumber: "",
    atcCode: "",
    vendorNumber: "",
    branch: "",
    plant: "",
    region: "",
    depot: "",
    month: format(new Date(), "M"), // current month, 1-12
    year: format(new Date(), "yyyy"),
    startDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    endDate: format(endOfMonth(new Date()), "yyyy-MM-dd"),
  };

  const { backendUrl } = useAuth();

  // The single source of truth for what's shown on screen AND in the Excel
  // export. Add/remove/relabel/reorder a field here — no JSX changes needed.
  const REPORT_COLUMNS = [
    { key: "InvoiceNo", label: "Invoice No.", group: "purchase" },
    {
      key: "vendorName",
      label: "Vendor",
      group: "vendorLedger",
      render: row => row?.vendor?.CompanyName || "-",
    },
    // {
    //   key: "vendorLedgerBalance",
    //   label: "Vendor Ledger Balance",
    //   group: "vendorLedger",
    //   render: row => formatMoney(row?.vendorLedger?.Balance),
    // },
    {
      key: "vendorInstallmentCount",
      label: "Installments",
      group: "vendorPaymentDue",
      render: row => row?.vendorPaymentDue?.length || "-",
    },
    {
      key: "vendorAmountDue",
      label: "Vendor Amount Due",
      group: "vendorPaymentDue",
      render: row =>
        formatMoney(sumInstallments(row?.vendorPaymentDue, "AmountDue")),
    },
    {
      key: "vendorAmountPaid",
      label: "Vendor Amount Paid",
      group: "vendorPaymentDue",
      render: row =>
        formatMoney(sumInstallments(row?.vendorPaymentDue, "AmountPaid")),
    },
    {
      key: "vendorPaymentBalance",
      label: "Vendor Payment Balance",
      group: "vendorPaymentDue",
      render: row =>
        formatMoney(sumInstallments(row?.vendorPaymentDue, "Balance")),
    },
    {
      key: "nextInstallmentDue",
      label: "Next Installment Due",
      group: "vendorPaymentDue",
      render: row => formatDateValue(nextInstallmentDue(row?.vendorPaymentDue)),
    },
    {
      key: "vendorPaymentStatus",
      label: "Vendor Payment Status",
      group: "vendorPaymentDue",
      render: row => vendorPaymentDueStatus(row?.vendorPaymentDue),
    },
    {
      key: "PurchaseDate",
      label: "Purchase Date",
      group: "purchase",
      render: row => formatDateValue(row.PurchaseDate),
    },
    { key: "ATC_Code", label: "ATC Code", group: "purchase" },
    { key: "Quantity", label: "Qty Purchased", group: "purchase" },
    {
      key: "UnitPrice",
      label: "Unit Cost",
      group: "purchase",
      render: row => formatMoney(row.UnitPrice),
    },
    {
      key: "batchBarCode",
      label: "Batch/Lot No.",
      group: "batch",
      render: row => row?.purchaseBatchItem?.Bar_Code || "-",
    },
    {
      key: "batchStatus",
      label: "Batch Status",
      group: "batch",
      render: row => row?.purchaseBatchItem?.ATC_Status || "-",
    },
    {
      key: "ATC_Plant",
      label: "Plant",
      group: "batch",
      render: row => row?.purchaseBatchItem?.ATC_Plant || "-",
    },
    {
      key: "ATC_Region",
      label: "Region",
      group: "batch",
      render: row => row?.purchaseBatchItem?.ATC_Region || "-",
    },
    {
      key: "ATC_Depot",
      label: "Depot",
      group: "batch",
      render: row => row?.purchaseBatchItem?.ATC_Depot || "-",
    },
    {
      key: "itemName",
      label: "Item",
      group: "items",
      render: row =>
        row?.purchaseBatchItem?.Stock_Batch_Items?.Item_Name || "-",
    },
    {
      key: "saleTransactionID",
      label: "Sale Invoice",
      group: "sale",
      render: row =>
        row?.sale?.TransactionID ? (
          <a
            href={`${backendUrl}/api/invoice/pdf/invoice/${
              row?.sale?.TransactionID
            }`}
            target="_blank"
          >
            {row?.sale?.TransactionID}
          </a>
        ) : (
          "Not Sold"
        ),
    },
    {
      key: "customer",
      label: "Customer",
      group: "sale",
      render: row => (row?.sale ? customerFullName(row.sale.customer) : "-"),
    },
    {
      key: "QtySold",
      label: "Qty Sold",
      group: "sale",
      render: row => (row?.sale ? row.sale.QTY : "-"),
    },
    {
      key: "PriceSold",
      label: "Price Sold",
      group: "sale",
      render: row => (row?.sale ? formatMoney(row.sale.PriceSold) : "-"),
    },
    {
      key: "PaymentDueAmount",
      label: "Customer Amount Due",
      group: "payment",
      render: row =>
        row?.paymentDue ? formatMoney(row.paymentDue.AmountDue) : "-",
    },
    {
      key: "PaymentDueDate",
      label: "Due Date",
      group: "payment",
      render: row =>
        row?.paymentDue ? formatDateValue(row.paymentDue.DueDate) : "-",
    },
  ];

  const [showFilter, setShowFilter] = useLocalStorage("showFilter", true);
  const [excelData, setExcelData] = useState([]);
  const [showVendorPopover, setShowVendorPopover] = useState(false);

  const CSVLinkRef = useRef(null);

  const [isfetchingExcel, fetchExcelData] = useDownloadExcel(
    excelData,
    CSVLinkRef
  );

  const [queryParams, setQueryParams] = useQueryParams({
    page: 1,
    limit: 40,
    ...initialFilterParams,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);
  useEffect(() => {
    setQueryParams({
      ...queryParams,
      ...debouncedFilterParams,
    });
  }, [debouncedFilterParams]);

  const fetchReport = async queryParams => {
    let response = await fetch(
      `${backendUrl}/api/vendors/atc-purchase-sales-payment-report?${queryString.stringify(
        queryParams
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    data.branch = [
      { label: "All", value: "" },
      ...data.branch
        .map(el => ({ label: el.Branch, value: el.Branch }))
        .filter(el => Boolean(el.value)),
    ];

    return data;
  };

  const { data = EMPTY_REPORT_DATA, refetch, isSuccess, isFetching } = useQuery(
    ["ATC_PURCHASE_SALES_PAYMENT", queryParams],
    () => fetchReport(queryParams),
    { keepPreviousData: true }
  );

  // Pinned columns stick to the left of the table so they stay visible while
  // scrolling through the many columns this report has. Persisted so the
  // layout a user sets up survives a reload.
  const [pinnedColumns, setPinnedColumns] = useLocalStorage(
    "atcPurchaseSalesPaymentPinnedColumns",
    []
  );
  const [pinnedOffsets, setPinnedOffsets] = useState({});
  const pinnedHeaderRefs = useRef({});

  const togglePinColumn = key =>
    setPinnedColumns(
      pinnedColumns.includes(key)
        ? pinnedColumns.filter(k => k !== key)
        : [...pinnedColumns, key]
    );

  // Pinned columns render first (in the order they were pinned), followed by
  // the rest of the columns in their original order.
  const orderedColumns = useMemo(() => {
    const pinned = pinnedColumns
      .map(key => REPORT_COLUMNS.find(c => c.key === key))
      .filter(Boolean);
    const rest = REPORT_COLUMNS.filter(c => !pinnedColumns.includes(c.key));
    return [...pinned, ...rest];
  }, [pinnedColumns]);

  // Consecutive columns sharing a group (and the same pinned/unpinned side of
  // the table) are collapsed into a single colSpan header cell. Pinned status
  // is part of the split so a spanning cell never straddles the boundary
  // between the sticky-left pinned columns and the normally-flowing rest —
  // that boundary can otherwise fall mid-group after a column gets pinned.
  const columnGroupRuns = useMemo(() => {
    const runs = [];
    orderedColumns.forEach(c => {
      const isPinned = pinnedColumns.includes(c.key);
      const last = runs[runs.length - 1];
      if (last && last.group === c.group && last.pinned === isPinned) {
        last.span += 1;
        last.keys.push(c.key);
      } else {
        runs.push({ group: c.group, pinned: isPinned, span: 1, keys: [c.key] });
      }
    });
    return runs;
  }, [orderedColumns, pinnedColumns]);

  // Column widths are content-driven (nowrap cells), so the left offset each
  // pinned column needs is measured from the actual rendered header cells
  // rather than assumed.
  useLayoutEffect(() => {
    let left = 0;
    const offsets = {};
    pinnedColumns.forEach(key => {
      offsets[key] = left;
      left += pinnedHeaderRefs.current[key]?.offsetWidth || 0;
    });

    setPinnedOffsets(prev => {
      const keys = Object.keys(offsets);
      const unchanged =
        keys.length === Object.keys(prev).length &&
        keys.every(key => prev[key] === offsets[key]);
      return unchanged ? prev : offsets;
    });
  }, [pinnedColumns, data.rows]);

  const pinnedCellStyle = column => {
    const base = cellStyle(column.group);
    if (!pinnedColumns.includes(column.key)) return base;

    const isLastPinned = pinnedColumns[pinnedColumns.length - 1] === column.key;

    return {
      ...base,
      position: "sticky",
      left: pinnedOffsets[column.key] ?? 0,
      zIndex: 1,
      boxShadow: isLastPinned ? "2px 0 4px -2px rgba(0,0,0,0.35)" : undefined,
    };
  };

  const fetchSetUpData = async () => {
    let response = await fetch(`${backendUrl}/api/incentive/set-up`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    data.region = data.region.map(el => ({
      value: el.region,
      label: el.region,
    }));
    data.plant = data.plant.map(el => ({ value: el.Plant, label: el.Plant }));
    data.depot = data.depot.map(el => ({ value: el.Depot, label: el.Depot }));

    return data;
  };

  const { data: setUpData = { region: [], plant: [], depot: [] } } = useQuery(
    ["INCENTIVE_SETUP"],
    () => fetchSetUpData(),
    { keepPreviousData: true }
  );

  const { limit, page, ...rest } = queryParams;
  const onDownloadExcelData = async () => {
    let exData = await fetchExcelData(
      `${backendUrl}/api/vendors/atc-purchase-sales-payment-report?${queryString.stringify(
        rest
      )}`,
      "GET"
    );

    const company = exData.data?.company?.CompName;

    const excelRows = (exData.data?.rows || []).map(row =>
      REPORT_COLUMNS.map(c => (c.render ? c.render(row) : row[c.key] ?? "-"))
    );

    const date =
      rest.startDate && rest.endDate
        ? `Date Prepared: Between ${format(
            new Date(rest.startDate),
            "E MMM d yyyy"
          )} to ${format(new Date(rest.endDate), "E MMM d yyyy")}`
        : "";

    setExcelData([
      [company],
      ["ATC Purchase, Sales, and Payment Report"],
      [date],
      [""],
      REPORT_COLUMNS.map(c => c.label),
      ...excelRows,
    ]);
  };

  const handleFilterParamsChange = e => {
    setFilterParams({
      ...filterParams,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  };

  const handleSearchQueryChange = e => {
    setQueryParams({
      ...queryParams,
      [e.target.name]: e.target.value,
    });
  };

  const search = () => {
    setQueryParams({
      ...queryParams,
      ...filterParams,
    });
  };

  const reset = () => {
    setFilterParams(initialFilterParams);
    setQueryParams({
      ...queryParams,
      ...initialFilterParams,
    });
  };

  const handleSelectedVendor = vendor => {
    setFilterParams({
      ...filterParams,
      vendorNumber: vendor.Vendor_ID,
    });
    setShowVendorPopover(false);
  };

  return (
    <main className="cash-book">
      <div className="content">
        <div className="content-main">
          <div className={`filter-area ${showFilter ? "open" : "close"}`}>
            <div className="filter-header">
              <h2>
                <FilterTwoIcon /> Filters
              </h2>
              <Button
                variant="white"
                onClick={() => setShowFilter(!showFilter)}
              >
                <FilterCollapseIcon />
              </Button>
            </div>
            <div className="filter-body">
              <Form>
                <div className="body">
                  <Form.Group className="mb-2-5">
                    <Form.Label>Invoice No.</Form.Label>
                    <Form.Control
                      name="invoiceNumber"
                      placeholder="Search Invoice No..."
                      value={filterParams.invoiceNumber}
                      onChange={e => handleFilterParamsChange(e)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2-5">
                    <Form.Label>ATC Code</Form.Label>
                    <Form.Control
                      name="atcCode"
                      placeholder="Search ATC Code..."
                      value={filterParams.atcCode}
                      onChange={e => handleFilterParamsChange(e)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2-5">
                    <Form.Label>Vendor ID</Form.Label>
                    <InputGroup>
                      <Form.Control
                        name="vendorNumber"
                        placeholder="Enter Vendor ID"
                        value={filterParams?.vendorNumber}
                        onChange={() => {}}
                        readOnly
                      />
                      <Popover
                        isOpen={showVendorPopover}
                        onClickOutside={() => setShowVendorPopover(false)}
                        content={() => (
                          <VendorSelector
                            handleSelectedCustomer={handleSelectedVendor}
                            setShowCustomerSelector={setShowVendorPopover}
                          />
                        )}
                        position="bottom"
                      >
                        <InputGroup.Text
                          onClick={() =>
                            setShowVendorPopover(!showVendorPopover)
                          }
                        >
                          <MagnifyIcon />
                        </InputGroup.Text>
                      </Popover>
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-2-5">
                    <Form.Label>Branch</Form.Label>
                    <Form.Select
                      name="branch"
                      value={filterParams?.branch}
                      onChange={e => handleFilterParamsChange(e)}
                    >
                      {(data?.branch || []).map(el => (
                        <option key={el.value} value={el.value}>
                          {el.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <hr className="mt-1 mb-4" />

                  <Form.Group className="mb-2-5">
                    <Form.Label>Plant</Form.Label>
                    <Select
                      classNamePrefix="form-select"
                      placeholder="All"
                      isClearable
                      theme={reactSelectTheme}
                      isSearchable={true}
                      key={`plant-${filterParams?.plant}`}
                      onChange={selected => {
                        setFilterParams({
                          ...filterParams,
                          plant: selected?.value || "",
                        });
                      }}
                      defaultValue={setUpData.plant.find(
                        el => el.value === filterParams?.plant
                      )}
                      options={setUpData.plant}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2-5">
                    <Form.Label>Region</Form.Label>
                    <Select
                      classNamePrefix="form-select"
                      placeholder="All"
                      isClearable
                      theme={reactSelectTheme}
                      isSearchable={true}
                      key={`region-${filterParams?.region}`}
                      onChange={selected => {
                        setFilterParams({
                          ...filterParams,
                          region: selected?.value || "",
                        });
                      }}
                      defaultValue={setUpData.region.find(
                        el => el.value === filterParams?.region
                      )}
                      options={setUpData.region}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2-5">
                    <Form.Label>Depot</Form.Label>
                    <Select
                      classNamePrefix="form-select"
                      placeholder="All"
                      isClearable
                      theme={reactSelectTheme}
                      isSearchable={true}
                      key={`depot-${filterParams?.depot}`}
                      onChange={selected => {
                        setFilterParams({
                          ...filterParams,
                          depot: selected?.value || "",
                        });
                      }}
                      defaultValue={setUpData.depot.find(
                        el => el.value === filterParams?.depot
                      )}
                      options={setUpData.depot}
                    />
                  </Form.Group>
                </div>

                <div className="buttons rounded">
                  <Button onClick={() => reset()} variant="white">
                    Reset
                  </Button>
                  <Button onClick={() => search()} variant="primary">
                    Search
                  </Button>
                </div>
              </Form>
            </div>
          </div>

          <div className="content-body">
            <header>
              <h1>
                {!showFilter && (
                  <button
                    onClick={() => setShowFilter(!showFilter)}
                    className="btn filter"
                  >
                    <FilterTwoIcon />
                  </button>
                )}
                Purchase, Sales, and Payment
                <button onClick={() => refetch()} className="btn text-primary">
                  <CachedIcon />
                </button>
              </h1>
              <div className="actions mr-5">
                <div style={{ zIndex: 10 }}>
                  <MonthYearPicker
                    onMonthChange={month => {
                      const monthIndex = Number(month) - 1;
                      setFilterParams({
                        ...filterParams,
                        month: month || "",
                        startDate: format(
                          startOfMonth(new Date(filterParams.year, monthIndex)),
                          "yyyy-MM-dd"
                        ),
                        endDate: format(
                          endOfMonth(new Date(filterParams.year, monthIndex)),
                          "yyyy-MM-dd"
                        ),
                      });
                    }}
                    onYearChange={year => {
                      const monthIndex = Number(filterParams.month) - 1;
                      setFilterParams({
                        ...filterParams,
                        year: year || "",
                        startDate: format(
                          startOfMonth(new Date(year, monthIndex)),
                          "yyyy-MM-dd"
                        ),
                        endDate: format(
                          endOfMonth(new Date(year, monthIndex)),
                          "yyyy-MM-dd"
                        ),
                      });
                    }}
                    yearValue={filterParams?.year}
                    monthValue={Number(filterParams?.month)}
                    variant="filter"
                    monthLabel=""
                    yearLabel=""
                  />
                </div>

                <CSVLink
                  className="btn print d-none"
                  filename={`ATC Purchase Sales Payment(${format(
                    new Date(),
                    "dd-MMM-yyyy hh:mm:ss a"
                  )}).csv`}
                  data={excelData}
                  ref={CSVLinkRef}
                />

                <Dropdown>
                  <Dropdown.Toggle
                    variant=""
                    className="btn print"
                    disabled={isfetchingExcel}
                    bsPrefix=""
                  >
                    <ExportIcon color="#008000" />
                    Export
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    popperConfig={{ strategy: "fixed" }}
                    renderOnMount
                    className="text-center"
                  >
                    <Dropdown.Item
                      as="div"
                      onClick={onDownloadExcelData}
                      className="p-cursor"
                    >
                      Excel <ExcelIcon color="#008000" />
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </header>

            <div className="px-md-4">
              <Table responsive borderless className="product-table">
                <thead>
                  <tr className="border-bottom">
                    <th />
                    {columnGroupRuns.map(run => {
                      const g = COLUMN_GROUPS[run.group];
                      const firstKey = run.keys[0];
                      const lastKey = run.keys[run.keys.length - 1];
                      const isLastPinned =
                        run.pinned &&
                        pinnedColumns[pinnedColumns.length - 1] === lastKey;

                      return (
                        <th
                          key={firstKey}
                          colSpan={run.span}
                          style={{
                            ...COMPACT_CELL_STYLE,
                            backgroundColor: g.color,
                            textAlign: "center",
                            fontWeight: 600,
                            ...(run.pinned
                              ? {
                                  position: "sticky",
                                  left: pinnedOffsets[firstKey] ?? 0,
                                  zIndex: 1,
                                  boxShadow: isLastPinned
                                    ? "2px 0 4px -2px rgba(0,0,0,0.35)"
                                    : undefined,
                                }
                              : {}),
                          }}
                        >
                          {g.label}
                        </th>
                      );
                    })}
                  </tr>
                  <tr>
                    <th>S/N</th>
                    {orderedColumns.map(c => {
                      const isPinned = pinnedColumns.includes(c.key);
                      return (
                        <th
                          key={c.key}
                          ref={el => {
                            if (el) pinnedHeaderRefs.current[c.key] = el;
                          }}
                          style={pinnedCellStyle(c)}
                        >
                          <span className="d-inline-flex align-items-center">
                            {c.label}
                            <button
                              type="button"
                              onClick={() => togglePinColumn(c.key)}
                              title={isPinned ? "Unpin column" : "Pin column"}
                              className="btn p-0 ms-1 d-inline-flex pin-column-btn"
                              style={{
                                lineHeight: 0,
                                opacity: isPinned ? 1 : 0.4,
                                color: isPinned ? "#0d6efd" : "inherit",
                              }}
                            >
                              {isPinned ? (
                                <PinIcon size={13} />
                              ) : (
                                <PinOutlineIcon size={13} />
                              )}
                            </button>
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, index) => (
                    <tr
                      key={`${row.PurchaseID}-${row.sale?.TransactionID ||
                        index}`}
                      className="border-bottom"
                    >
                      <t>{index + 1}</t>
                      {orderedColumns.map(c => (
                        <td key={c.key} style={pinnedCellStyle(c)}>
                          {c.render ? c.render(row) : row[c.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>

              {!isFetching && isSuccess && isEmpty(data?.rows) ? (
                <NoTableItem queryParams={queryParams} />
              ) : null}
            </div>

            <div className="d-flex justify-content-between px-3 align-items-center pagination">
              <div className="pagination_left">
                <p className="m-0 p-0">Show</p>
                <select
                  value={queryParams.limit}
                  name="limit"
                  className="form-select"
                  onChange={e => handleSearchQueryChange(e)}
                >
                  <option value="10">10 rows</option>
                  <option value="20">20 rows</option>
                  <option value="30">30 rows</option>
                  <option value="40">40 rows</option>
                  <option value="50">50 rows</option>
                  <option value="100">100 rows</option>
                </select>
              </div>

              <ReactPaginate
                {...paginationOptions}
                pageCount={Math.ceil(data.count / queryParams.limit)}
                marginPagesDisplayed={2}
                pageRangeDisplayed={0}
                onPageChange={({ selected }) => {
                  document.body.scrollTop = document.documentElement.scrollTop = 0;
                  setQueryParams({
                    ...queryParams,
                    page: selected + 1,
                  });
                }}
                forcePage={queryParams.page - 1}
              />
            </div>
          </div>
        </div>
      </div>

      <ModalLoader show={isfetchingExcel} />
    </main>
  );
}
